import {ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    Output,
    ViewChild,
} from '@angular/core';

import {Log} from "ng2-logger";
import {formatDate} from '@angular/common';
import {AppUtils} from "@app/shared/util/app.utils";
import * as moment from "moment";

@Component({
    selector: "app-input-datepicker",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DatepickerComponent,
            multi: true
        }
    ],
    styleUrls: ['./datepicker.component.scss'],
    templateUrl: './datepicker.component.html'
})
export class DatepickerComponent implements ControlValueAccessor, AfterViewInit {
    _logger = Log.create(DatepickerComponent.name);

    @Input() label = '';
    @Input() labelAlign: 'top' | 'left' | 'floating' = 'top';
    @Input() placeholder = '';
    @Input() formControl: UntypedFormControl;
    @Input() showErrors = false;
    @Input() isDisabled = false;
    @Input() showIcon = true;
    @Input() inputDateFormat: 'dd/MM/yyyy' | 'timestamp' = 'dd/MM/yyyy';
    @Input() displayDateFormat = 'dd/mm/yy';
    @Input() outputDateFormat: 'dd/MM/YYYY HH:mm:ss' | 'timestamp' = 'timestamp';
    @Input() appendTo = '';
    @Input() selectionMode = 'single';
    @Input() id = AppUtils.generateUUID();
    @Input() showTime = false;
    @Input() hourFormat: '12' | '24' = '24';

    @HostBinding('class.m2') @Input() m2 = false;

    @Output() dateChange = new EventEmitter<any>();
    @ViewChild('input', {static: true}) input: ElementRef;

    private onChangeCallback: (_: any) => {};
    private onTouchedCallback: (_: any) => {};

    private selectedDate = null;

    @HostBinding('class') get labelAlignmentClass(): string {

        if (this.labelAlign === 'left') {
            return 'label-left';
        } else if (this.labelAlign === 'top') {
            return 'label-top';
        } else {
            return 'label-float.float-input'
        }
    }

    constructor() {
    }

    writeValue(value) {
        // TODO Hackerman fix for 1970 date
        if ((value === 0 || value === null) && this.selectedDate != null) {
            this.selectedDate = null;
            if (this.input) {
                this.input.nativeElement.value = '';
            }
            // ensure local state is cleared when form resets
            this.clear();
        } else {
            this._logger.info("writeValue::", value);
            this.updateDateValue(value);
        }
    }

    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }

    ngAfterViewInit() {
    }

    clear() {
        this.propagateChange(null);
        this.dateChange.emit(null);
    }

    // Handle the date selection
    onSelect(event: any) {
        switch (this.selectionMode) {
            case 'range':
                this.dateChange.emit(this.selectedDate);
                break;
            default:
                this.selectedDate = event;
                const formattedDate = this.formatDate(event);
                this.propagateChange(formattedDate);
                this.dateChange.emit(formattedDate);
        }
    }

    // Format the date according to the desired format
    private formatDate(date: Date): string {
        if (!date) {
            return null
        }
        if (this.outputDateFormat === 'timestamp') {
            return `${date.getTime()}`;
        } else {
            return formatDate(date, this.outputDateFormat, 'en-US');
        }
    }


    private parseDate(date: Date | string): Date | null {
        if (!date) {
            return null; // Handle null or undefined input
        }

        // If the input is already a Date object, return it directly
        if (date instanceof Date) {
            return date;
        }

        // Parse the string using the provided input format
        let parsedDate: moment.Moment;
        if (this.inputDateFormat === 'timestamp') {
            parsedDate = moment(new Date(date));
        } else {
            parsedDate = moment(date, this.inputDateFormat, false); // Strict parsing
        }

        // Return a valid Date object or null if parsing fails
        return parsedDate.isValid() ? parsedDate.toDate() : null;
    }

    // Update the internal date value and propagate changes
    private updateDateValue(date: any) {
        const parsedDate = this.parseDate(date);
        this.selectedDate = parsedDate;
        const formattedDate = this.formatDate(parsedDate);
        this.propagateChange(formattedDate);
    }

    private updateRangeValue(range: any[]) {

        if (range === null) {
            this.clear();
            return;
        }

        const startDate = this.parseDate(range[0]);
        const endDate = this.parseDate(range[1]);
        this.selectedDate = [startDate, endDate];
        const formattedStartDate = this.formatDate(startDate);
        const formattedEndDate = this.formatDate(endDate);
        this.propagateChange([formattedStartDate, formattedEndDate]);
    }

    // Propagate the formatted date value to the form control
    private propagateChange(value: any) {
        if (this.onChangeCallback) {
            this.onChangeCallback(value);
        }
    }

    get value() {
        return this.selectedDate;
    }

    set value(value: any[]) {
        if (this.selectionMode === 'range') {
            this.updateRangeValue(value);
        } else {
            this.updateDateValue(value);
        }
    }

    get hasError() {
        return this.formControl && this.formControl.errors && this.showErrors;
    }

    get leftLabel() {
        return this.label && this.labelAlign === 'left';
    }

    get topLabel() {
        return this.label && this.labelAlign === 'top';
    }

    get floatLabel() {
        return this.label && this.labelAlign === 'floating';
    }

    get determinePlaceholder() {
        return this.floatLabel ? '' : this.placeholder;
    }
}
